import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getContactInfoTemplate, createContactInfoTemplate, updateContactInfoTemplate } from '../services/api';
import {
  Box, Typography, Button, Stack, Paper, Alert, TextField, CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

export default function ContactInfoTemplatePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [fieldName, setFieldName] = useState('');
  const [icon, setIcon] = useState('');

  const loadTemplate = useCallback(async () => {
    try {
      const { data } = await getContactInfoTemplate(id);
      setFieldName(data.fieldName || '');
      setIcon(data.icon || '');
    } catch {
      setError('Failed to load field template');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!isNew) loadTemplate();
  }, [isNew, loadTemplate]);

  const handleSave = async () => {
    if (!fieldName.trim()) { setError('Field name is required'); return; }
    setError('');
    setSaving(true);
    try {
      const payload = { fieldName: fieldName.trim(), icon: icon.trim() };
      if (isNew) {
        await createContactInfoTemplate(payload);
      } else {
        await updateContactInfoTemplate(id, payload);
      }
      navigate('/management?tab=fields');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save field template');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/management?tab=fields')} sx={{ mb: 2 }}>
        Back to Management
      </Button>

      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {isNew ? 'New Field Template' : 'Edit Field Template'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Define a type of contact information that can be added to contact persons (e.g., LinkedIn, WhatsApp, Mobile Phone).
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => navigate('/management?tab=fields')}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isNew ? 'Create Template' : 'Save Changes'}
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 3, mb: 2.5 }}>
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>📋</span> Field Information
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The field name appears when users add contact information to a person. The icon is optional and used for display.
        </Typography>
        <Stack spacing={2}>
          <TextField
            size="small"
            fullWidth
            label="Field Name *"
            required
            value={fieldName}
            onChange={(e) => setFieldName(e.target.value)}
            placeholder="e.g. LinkedIn, WhatsApp, Mobile Phone"
          />
          <TextField
            size="small"
            fullWidth
            label="Icon"
            value={icon}
            onChange={(e) => setIcon(e.target.value)}
            placeholder="Optional icon identifier"
            helperText="An emoji or icon name to visually identify this field type."
          />
        </Stack>
      </Paper>

      <Paper elevation={3} sx={{ position: 'sticky', bottom: 0, p: 2, mt: 3, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button variant="outlined" onClick={() => navigate('/management?tab=fields')}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : isNew ? 'Create Template' : 'Save Changes'}
        </Button>
      </Paper>
    </Box>
  );
}
